<template>
  <div :class="$style.base" v-test="'booking-confirmation'">
    <div :class="$style.main">
      <div :class="$style.top">
        <IconCheckBox />
        <div :class="$style.heading">
          {{
            requestBooking
              ? $t('completed.requestHeading')
              : $t('completed.heading')
          }}
        </div>

        <div
          v-if="requestBooking"
          :class="$style.confirmationMessage"
          v-test="'request-confirmation'"
        >
          {{ $t('completed.confirmation_pending') }}
        </div>
      </div>
      <div :class="$style.summaryWrap">
        <div :class="$style.summary">
          <div :class="$style.summaryLeft">
            <div
              v-if="date"
              :class="[$style.row, $style.date]"
              v-test="'confirmed-date'"
            >
              {{ date }}
            </div>
            <div
              v-if="weekDay"
              :class="$style.row"
              v-test="'confirmed-weekday'"
            >
              <IconCalendar />
              {{ weekDay }}
            </div>
            <div v-if="time" :class="$style.row" v-test="'confirmed-time'">
              <IconClock />
              {{ time }}
            </div>
          </div>
          <div :class="$style.summaryRight">
            <div
              v-if="services.length"
              :class="$style.services"
              v-test="'confirmed-services'"
            >
              <div
                v-for="(service, index) in services"
                :key="`service${index}`"
              >
                <div v-if="service.name" :class="$style.row">
                  {{ service.name }}
                </div>
              </div>
            </div>

            <div
              v-if="resources && resources.length"
              v-test="'confirmed-resources'"
            >
              <div
                :class="$style.row"
                v-for="(resource, index) in resources"
                :key="`resource${index}`"
              >
                <BaseAvatar :employee="resource" small />
                <div :class="$style.resourceName">
                  {{ resource.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Share />
      <BookAgain />
    </div>
    <Footer />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';

import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import { useServicesStore } from '@/stores/services';

import BaseAvatar from '@/components/BaseAvatar.vue';
import IconCalendar from '@/components/IconCalendar.vue';
import IconCheckBox from '@/components/IconCheckBox.vue';
import IconClock from '@/components/IconClock.vue';
import BookAgain from '../shared/BookAgain.vue';
import Footer from './Footer.vue';
import Share from './Share.vue';

const { d } = useI18n();

const { bookingResponse } = useBookingStore();
const { requestBooking } = useCompanyStore();

const date = computed(() => {
  if (!bookingResponse?.start_at) {
    return null;
  }

  return d(new Date(bookingResponse.start_at), 'monthDay');
});

const weekDay = computed(() => {
  if (!bookingResponse?.start_at) {
    return null;
  }

  return d(new Date(bookingResponse.start_at), 'weekdayLong');
});

const time = computed(() => {
  if (!bookingResponse?.start_at) {
    return null;
  }

  return d(new Date(bookingResponse.start_at), 'time');
});

const resources = ref(bookingResponse?.resources || []);

const { fetchServices } = useServicesStore();
fetchServices();

const { services } = storeToRefs(useBookingStore());

window.parent.postMessage(
  {
    name: 'bookingCompleted',
    data: toRaw(bookingResponse)
  },
  '*'
);
</script>

<style lang="scss" module>
.base {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.main {
  padding: 0 $spacing $spacing;
}

.top {
  text-align: center;
}

.heading {
  font-size: 20px;
  margin-top: $spacing * 0.5;
}

.confirmationMessage {
  margin-top: $spacing;
}

.summaryWrap {
  padding: 0 $spacing;
}

.summary {
  display: table;
  margin: $spacing * 4 0 $spacing * 3;
}

.summaryLeft,
.summaryRight {
  display: table-cell;

  & > .row:last-child {
    margin-bottom: 0;
  }
}

.summaryLeft {
  padding-right: $spacing * 1.5;
}

.summaryRight {
  border-left: 1px solid rgba(0, 0, 0, 0.13);
  padding-left: $spacing * 1.5;
}

.row {
  display: flex;
  align-items: center;
  margin-bottom: $spacing;

  & > *:first-child {
    margin-right: 4px;
  }
}

.date,
.services {
  font-weight: bold;
}
</style>
